<template>
  <MainLayout>
    <ContentPageHeader functionName="ログ管理" pageName="イベントログ">
    </ContentPageHeader>
    
    <!-- 基本検索セクション -->
    <div class="search-section" @keyup.enter="search()">
      <div class="search-form">
        <FormSelectSearchField 
          label="販売チャンネル" 
          id="salesChannel" 
          v-model="searchParams.salesChannelId"
          :options="salesChannelOptions"
          placeholder="選択してください"
          size="medium"
        />
        
        <FormTextField 
          label="イベント" 
          id="eventType" 
          v-model="searchParams.eventType"
          size="medium"
        />
        
        <div class="date-range-container">
          <div class="date-field">
            <FormDateField 
              label="発生時間（From）" 
              id="timeFrom" 
              v-model="searchParams.timeFrom"
            />
          </div>
          <span class="date-range-separator">～</span>
          <div class="date-field">
            <FormDateField 
              label="発生時間（To）" 
              id="timeTo" 
              v-model="searchParams.timeTo"
            />
          </div>
        </div>
        
        <FormTextField 
          label="商品コード" 
          id="productCode" 
          v-model="searchParams.productCode"
          size="medium"
        />
        
        <FormTextField 
          label="商品名" 
          id="productName" 
          v-model="searchParams.productName"
          size="medium"
        />
      </div>
    </div>
    
    <!-- 検索ボタンエリア -->
    <div class="search-buttons-section">
      <div class="search-buttons">
        <button class="btn btn-secondary" @click="resetSearch">リセット</button>
        <button class="btn btn-primary" @click="logSearch">検索</button>
      </div>
    </div>
    
    <!-- 検索結果テーブル -->
    <div class="search-result">
      <div class="search-result-header">
        <h4>イベントログ一覧</h4>
      </div>
      <FormListTable
        :items="eventLogs"
        :columns="columns"
        :actions="actions"
        :perPage="perPage"
        :page="currentPage"
        :totalItems="totalCount"
        :emptyMessage="'ログがありません'"
        @page-change="onPageChange"
        @action="onAction"
      >
        <!-- カスタムヘッダーテンプレート -->
        <template v-for="col in columns.filter(col => col.sortable)" :key="`header-${col.key}`" #[`header-${col.key}`]>
          <div class="column-header" @click="sortBy(col.key)">
            {{ col.label }}
            <span class="sort-icon">
              <i v-if="sortKey === col.key && sortOrder === 'asc'" class="fa fa-sort-up" aria-hidden="true"></i>
              <i v-else-if="sortKey === col.key && sortOrder === 'desc'" class="fa fa-sort-down" aria-hidden="true"></i>
              <i v-else class="fa fa-sort" aria-hidden="true"></i>
            </span>
          </div>
        </template>
        
        <!-- 結果カラムのカスタム表示 -->
        <template #result="{ item }">
          <span :class="item.result === 1 ? 'result-success' : 'result-failed'">
            {{ item.result === 1 ? 'success' : 'failed' }}
          </span>
        </template>
        
        <!-- 現在店舗カラムのカスタム表示 -->
        <template #currentStore="{ item }">
          {{ getStoreNameById(item.currentStore) }}
        </template>
      </FormListTable>
    </div>
    
    <!-- イベントログ詳細モーダル -->
    <common-modal
      v-if="detailModalVisible"
      :visible="detailModalVisible"
      title="イベントログ詳細"
      :showOkButton="false"
      :showCancelButton="false"
      @close="closeDetailModal"
      class="event-log-detail-modal"
    >
      <div class="event-log-detail" v-if="selectedEventLog">
        <table class="detail-table">
          <tbody>
            <tr>
              <td class="label-cell">イベント</td>
              <td class="value-cell">{{ selectedEventLog.eventType }}</td>
            </tr>
            <tr>
              <td class="label-cell">発生時間</td>
              <td class="value-cell">{{ selectedEventLog.time }}</td>
            </tr>
            <tr>
              <td class="label-cell">結果</td>
              <td class="value-cell">
                <span :class="selectedEventLog.result === 1 ? 'result-success' : 'result-failed'">
                  {{ selectedEventLog.result === 1 ? 'success' : 'failed' }}
                </span>
              </td>
            </tr>
            <tr>
              <td class="label-cell">メッセージ</td>
              <td class="value-cell">
                <div class="scrollable-message" v-html="formatLog(selectedEventLog.log)"></div>
              </td>
            </tr>
            <tr>
              <td class="label-cell">商品コード</td>
              <td class="value-cell">{{ selectedEventLog.productCode }}</td>
            </tr>
            <tr>
              <td class="label-cell">商品名</td>
              <td class="value-cell">{{ selectedEventLog.productName }}</td>
            </tr>
            <tr>
              <td class="label-cell">現在店舗</td>
              <td class="value-cell">{{ getStoreNameById(selectedEventLog.currentStore) }}</td>
            </tr>
            <tr>
              <td class="label-cell">販売チャンネル</td>
              <td class="value-cell">{{ selectedEventLog.salesChannel }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </common-modal>
  </MainLayout>
</template>

<script>
import MainLayout from '@/components/layout/MainLayout.vue'
import ContentPageHeader from '@/components/layout/ContentPageHeader.vue'
import FormListTable from '@/components/form/FormListTable.vue'
import FormTextField from '@/components/form/FormTextField.vue'
import FormSelectSearchField from '@/components/form/FormSelectSearchField.vue'
import FormDateField from '@/components/form/FormDateField.vue'
import CommonModal from '@/components/common/CommonModal.vue'
import service from '../../services/service';
import { showLoading, hideLoading } from '@/utils/loadingEventBus';

export default {
  name: 'EventLogList',
  components: {
    MainLayout,
    ContentPageHeader,
    FormListTable,
    FormTextField,
    FormSelectSearchField,
    FormDateField,
    CommonModal
  },
  data() {
    return {
      // ページング
      currentPage: 0,
      perPage: 50,
      totalCount: 0,
      
      // ソート状態
      sortKey: null,
      sortOrder: 'asc',
      
      // 検索パラメータ
      searchParams: {
        salesChannelId: '',
        eventType: '',
        timeFrom: '',
        timeTo: '',
        productCode: '',
        productName: ''
      },
      
      // 販売チャンネルオプション
      salesChannelOptions: [],
      
      // 店舗オプション
      storeOptions: [],
      
      // イベントログ一覧
      eventLogs: [],
      
      // 詳細モーダル
      detailModalVisible: false,
      selectedEventLog: null,
      
      // テーブルカラム定義
      columns: [
        { key: 'eventType', label: 'イベント', sortable: true, minWidth: 80, maxWidth: 150 },
        { key: 'time', label: '発生時間', sortable: true, minWidth: 200, maxWidth: 220 },
        { key: 'result', label: '結果', sortable: true, minWidth: 80, maxWidth: 120 },
        { key: 'log', label: 'メッセージ', sortable: true, minWidth: 200, maxWidth: 600 },
        { key: 'productCode', label: '商品コード', sortable: true, minWidth: 120, maxWidth: 150 },
        { key: 'productName', label: '商品名', sortable: true, minWidth: 100, maxWidth: 400 },
        { key: 'currentStore', label: '現在店舗', sortable: true, minWidth: 140, maxWidth: 180 },
        { key: 'salesChannel', label: '販売チャンネル', sortable: true, minWidth: 150, maxWidth: 170 }
      ],
      
      // 操作ボタン定義
      actions: [
        {
          label: '',
          icon: 'fa-info-circle',
          buttonClass: 'btn-primary',
          action: (item) => this.showDetailModal(item),
        }
      ]
    }
  },
  methods: {

    // 検索条件を保存
    saveSearchConditions() {
      const conditions = {
        searchParams: { ...this.searchParams },
        currentPage: this.currentPage,
        perPage: this.perPage,
        sortKey: this.sortKey,
        sortOrder: this.sortOrder
      };
      localStorage.setItem('eventLogSearchConditions', JSON.stringify(conditions));
    },
    
    // 検索条件を復元
    loadSearchConditions() {
      const saved = localStorage.getItem('eventLogSearchConditions');
      if (saved) {
        const conditions = JSON.parse(saved);
        this.searchParams = { ...conditions.searchParams };
        this.currentPage = conditions.currentPage || 0;
        this.perPage = conditions.perPage || 50;
        this.sortKey = conditions.sortKey || null;
        this.sortOrder = conditions.sortOrder || 'asc';
      }
    },

    // イベントログデータのフォーマット処理
    formatEventLogs(logs) {
      return logs.map(log => {
        // 日時フォーマット
        const formattedLog = { ...log };
        
        if (formattedLog.time) {
          formattedLog.time = this.formatDateTime(formattedLog.time);
        }
        
        return formattedLog;
      });
    },
    
    // 日時フォーマット（yyyy/mm/dd hh:mm:ss）
    formatDateTime(dateTimeStr) {
      // 早期リターン（ガード節）
      if (!dateTimeStr) return '';
      
      const date = new Date(dateTimeStr);
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
    },
    
    // 検索処理
    async search(page) {
      try {
        showLoading();

        // ページングの更新
        if (page !== undefined) {
          this.currentPage = page;
        } else {
          this.currentPage = 0;
        }

        // 検索パラメータの構築
        const params = new URLSearchParams();
        
        this.searchParams.salesChannelId && params.append('salesChannelId', this.searchParams.salesChannelId);
        this.searchParams.eventType && params.append('eventType', this.searchParams.eventType);
        this.searchParams.timeFrom && params.append('timeFrom', this.searchParams.timeFrom);
        this.searchParams.timeTo && params.append('timeTo', this.searchParams.timeTo);
        this.searchParams.productCode && params.append('productCode', this.searchParams.productCode);
        this.searchParams.productName && params.append('productName', this.searchParams.productName);
        
        // ページングパラメータ
        params.append('page', this.currentPage);
        params.append('perPage', this.perPage);
        
        // APIリクエスト
        const response = await service.searchEventLogs(params);
        
        if (!response.data.success) {
          console.error('イベントログ検索エラー:', response.data.message);
          return;
        }
        
        this.eventLogs = this.formatEventLogs(response.data.eventLogs);
        this.totalCount = response.data.totalCount;
      } catch (error) {
        console.error('イベントログ検索中にエラーが発生しました', error);
        alert('イベントログの検索中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
      
      // 検索条件を保存
      this.saveSearchConditions();
    },
    
    // 検索ボタン押下処理
    logSearch() {
      this.search();
    },

    // 検索条件リセット
    resetSearch() {
      this.searchParams = {
        salesChannelId: '',
        eventType: '',
        timeFrom: '',
        timeTo: '',
        productCode: '',
        productName: ''
      };
      this.currentPage = 0;
      this.search();
    },
    
    // ページ変更ハンドラ
    onPageChange(page) {
      this.search(page - 1);
    },
    
    // 検索条件があるかチェック
    hasSearchParams() {
      return this.searchParams.salesChannelId ||
             this.searchParams.eventType ||
             this.searchParams.timeFrom ||
             this.searchParams.timeTo ||
             this.searchParams.productCode ||
             this.searchParams.productName;
    },
    
    // ソート処理
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortOrder = 'asc';
      }
      
      this.multiLevelSort();
    },
    
    // 複数レベルのソート処理
    multiLevelSort() {
      this.eventLogs.sort((a, b) => {
        // 主ソートキーによる比較
        const primaryResult = this.compareValues(a, b, this.sortKey, this.sortOrder);
        if (primaryResult !== 0) return primaryResult;
        
        // 第二ソートキー: 結果（昇順）
        const secondaryResult = this.compareValues(a, b, 'result', 'asc');
        if (secondaryResult !== 0) return secondaryResult;
        
        // 第三ソートキー: 現在店舗
        return this.compareValues(a, b, 'currentStore', 'asc');
      });
    },
    
    // 値の比較ヘルパー関数
    compareValues(a, b, key, order) {
      let valueA = a[key];
      let valueB = b[key];
      
      valueA = typeof valueA === 'string' ? valueA.toLowerCase() : valueA;
      valueB = typeof valueB === 'string' ? valueB.toLowerCase() : valueB;
      
      if (valueA < valueB) return order === 'asc' ? -1 : 1;
      if (valueB < valueA) return order === 'asc' ? 1 : -1;
      return 0;
    },
    // 初期データ取得処理
    async getInitData() {
      try {
        showLoading();
        // 販売チャンネル一覧を取得
        const salesChannelResponse = await service.getSalesChannelList();
        if (!salesChannelResponse || !salesChannelResponse.data) {
          return;
        }
        this.salesChannelOptions = salesChannelResponse.data.salesChannels.map(channel => ({
          value: channel.id,
          label: channel.acc
        }));
        // 店舗一覧を取得
        const storeResponse = await service.getStoreList();
        if (!storeResponse || !storeResponse.data) {
          return;
        }
        this.storeOptions = storeResponse.data.stores.map(store => ({
          value: store.id.toString(),
          label: store.storeName
        }));
      } catch (error) {
        console.error('初期情報の取得に失敗しました:', error);
        alert('初期情報の取得中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    
    // 店舗IDから店舗名を取得
    getStoreNameById(storeId) {
      if (!storeId) return '';
      const store = this.storeOptions.find(option => option.value === storeId.toString());
      return store ? store.label : storeId;
    },
    
    // 詳細モーダル表示
    showDetailModal(item) {
      this.selectedEventLog = item;
      this.detailModalVisible = true;
    },
    
    // 詳細モーダルを閉じる
    closeDetailModal() {
      this.detailModalVisible = false;
      this.selectedEventLog = null;
    },
    
    // アクションハンドラ
    onAction({ action, item }) {
      if (action === 'fa-info-circle') {
        this.showDetailModal(item);
      }
    },

    // /nで改行
    formatLog(log) {
      if (!log) return '';
      return log.replace(/\n/g, '<br>');
    }
  },
  mounted() {
    // 検索条件を復元
    this.loadSearchConditions();

    // コンポーネントがマウントされたらデータを取得
    this.getInitData();
    
    // デフォルトのソート設定
    this.sortKey = 'time';
    this.sortOrder = 'desc';
    
    this.search();
  }
}
</script>

<style scoped>
.column-header {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sort-icon {
  margin-left: 5px;
}

.result-success {
  color: green;
  font-weight: bold;
}

.result-failed {
  color: red;
  font-weight: bold;
}

/* 詳細モーダルのスタイル */
.event-log-detail-modal :deep(.common-modal-content) {
  max-width: 1000px;
}

.event-log-detail {
  width: 100%;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #dee2e6;
}

.detail-table tr {
  border-bottom: 2px solid #dee2e6;
}

.detail-table tr:last-child {
  border-bottom: none;
}

.label-cell {
  width: 30%;
  padding: 12px;
  font-weight: bold;
  vertical-align: top;
  background-color: #e9ecef;
  border-right: 2px solid #dee2e6;
  color: #495057;
}

.value-cell {
  width: 70%;
  padding: 12px;
  word-break: break-word;
  background-color: #ffffff;
}

.scrollable-message {
  max-height: 150px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background-color: #f8f9fa;
}
</style>
